'use client';
import React, { useState } from 'react';
import {
  User, FileText, Image as ImageIcon, Calendar, Mail, Phone,
  Award, MapPin, ArrowUpRight, ChevronDown, ChevronUp,
  Clock, CheckCircle, Circle, Download,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

// Inline LinkedIn icon (brand icons removed from lucide-react v1)
const LinkedinIcon = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);


// ─── Types ────────────────────────────────────────────────────────────────────

interface DirectorDocument {
  name: string;
  type: string;
  size: string;
  date: string;
  category: 'bio' | 'credential' | 'report' | 'legal';
}

interface DirectorImage {
  src: string;
  alt: string;
  caption: string;
}

interface ScheduleItem {
  date: string;
  time: string;
  title: string;
  type: 'webinar' | 'site-visit' | 'q&a' | 'meeting';
  status: 'upcoming' | 'completed' | 'live';
  description: string;
}

interface ProjectDirector {
  name: string;
  title: string;
  avatar: string;
  bio: string;
  experience: string;
  location: string;
  email: string;
  phone: string;
  linkedin: string;
  credentials: string[];
  documents: DirectorDocument[];
  images: DirectorImage[];
  schedule: ScheduleItem[];
}

interface ProjectDirectorSectionProps {
  campaignId: string;
}

// ─── Mock Director Data ───────────────────────────────────────────────────────

const directorData: Record<string, ProjectDirector> = {
  'camp-001': {
    name: 'Khalid Al-Rashidi',
    title: 'Project Director — Infrastructure',
    avatar: '/assets/images/no_image.png',
    bio: 'Khalid brings 18 years of infrastructure finance and project management experience across the GCC and MENA region. He has led over $3.2B in infrastructure transactions, specialising in logistics, industrial, and transport assets. Prior to joining Meridian, he held senior roles at ADNOC and Mubadala Infrastructure.',
    experience: '18 years',
    location: 'Dubai, UAE',
    email: 'k.rashidi@meridianinfra.ae',
    phone: '+971 4 XXX XXXX',
    linkedin: 'linkedin.com/in/khalid-rashidi',
    credentials: ['CFA Charterholder', 'PMP Certified', 'MSc Finance — LBS', 'RICS Associate'],
    documents: [
      { name: 'Director CV & Biography', type: 'PDF', size: '420 KB', date: '2026-07-10', category: 'bio' },
      { name: 'Professional Credentials', type: 'PDF', size: '180 KB', date: '2026-07-10', category: 'credential' },
      { name: 'Q2 2026 Project Update Report', type: 'PDF', size: '1.2 MB', date: '2026-07-28', category: 'report' },
      { name: 'Director Disclosure Statement', type: 'PDF', size: '95 KB', date: '2026-07-15', category: 'legal' },
    ],
    images: [
      { src: '/assets/images/image-1787244773683.png', alt: 'Meridian Logistics Hub site overview — aerial view of the 280,000 sqm campus under development', caption: 'Site Overview — Jebel Ali Free Zone' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Construction progress at Meridian Logistics Hub showing completed structural framework', caption: 'Construction Progress — Phase 1' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Meridian Logistics Hub interior showing warehouse racking and loading bay infrastructure', caption: 'Interior Fit-Out — Warehouse A' },
    ],
    schedule: [
      { date: '2026-08-28', time: '14:00 GST', title: 'Investor Webinar — Q3 Project Update', type: 'webinar', status: 'upcoming', description: 'Quarterly update covering construction milestones, tenant confirmations, and financial performance.' },
      { date: '2026-09-05', time: '10:00 GST', title: 'Site Visit — JAFZA Campus', type: 'site-visit', status: 'upcoming', description: 'Guided site tour for qualified investors. Limited to 20 participants. Registration required.' },
      { date: '2026-09-12', time: '15:00 GST', title: 'Live Q&A with Project Director', type: 'q&a', status: 'upcoming', description: 'Open Q&A session with Khalid Al-Rashidi. Submit questions in advance via the investor portal.' },
      { date: '2026-07-30', time: '14:00 GST', title: 'Soft Close Investor Briefing', type: 'webinar', status: 'completed', description: 'Briefing following the 75% soft close milestone, covering next steps and drawdown schedule.' },
    ],
  },
  'camp-002': {
    name: 'Sarah Mitchell',
    title: 'Project Director — Real Estate',
    avatar: '/assets/images/no_image.png',
    bio: 'Sarah is a seasoned real estate investment professional with 15 years of experience managing commercial property portfolios across the GCC. She oversees all aspects of the Ashford Commercial REIT II, from asset acquisition and tenant management to investor relations and regulatory compliance. Previously at JLL and CBRE.',
    experience: '15 years',
    location: 'Abu Dhabi, UAE',
    email: 's.mitchell@ashfordcapital.ae',
    phone: '+971 2 XXX XXXX',
    linkedin: 'linkedin.com/in/sarah-mitchell-re',
    credentials: ['MRICS', 'CFA Level II', 'MBA — INSEAD', 'RICS Registered Valuer'],
    documents: [
      { name: 'Director Profile & CV', type: 'PDF', size: '380 KB', date: '2026-06-08', category: 'bio' },
      { name: 'RICS Membership Certificate', type: 'PDF', size: '120 KB', date: '2026-06-08', category: 'credential' },
      { name: 'Portfolio Performance Report H1 2026', type: 'PDF', size: '2.1 MB', date: '2026-07-15', category: 'report' },
      { name: 'Conflict of Interest Disclosure', type: 'PDF', size: '88 KB', date: '2026-06-10', category: 'legal' },
    ],
    images: [
      { src: '/assets/images/image-1787244773683.png', alt: 'Grade-A office tower in Abu Dhabi — flagship asset of Ashford Commercial REIT II portfolio', caption: 'Flagship Asset — Abu Dhabi CBD' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Mixed-use commercial development in Dubai showing retail and office spaces', caption: 'Mixed-Use Asset — Dubai' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Riyadh commercial office building recently acquired for the REIT portfolio', caption: 'Recent Acquisition — Riyadh' },
    ],
    schedule: [
      { date: '2026-08-30', time: '11:00 GST', title: 'Portfolio Review Webinar', type: 'webinar', status: 'upcoming', description: 'Comprehensive review of all 6 portfolio assets, occupancy updates, and upcoming acquisitions.' },
      { date: '2026-09-10', time: '09:00 GST', title: 'Asset Tour — Abu Dhabi CBD', type: 'site-visit', status: 'upcoming', description: 'Investor site visit to the flagship Abu Dhabi office tower. Pre-registration required.' },
      { date: '2026-07-10', time: '14:00 GST', title: '50% Milestone Investor Update', type: 'webinar', status: 'completed', description: 'Update following the 50% subscription milestone with revised acquisition pipeline.' },
    ],
  },
  'camp-003': {
    name: 'Dr. Amira Hassan',
    title: 'Project Director — Clean Energy',
    avatar: '/assets/images/no_image.png',
    bio: 'Dr. Hassan is a renewable energy specialist with a PhD in Energy Systems Engineering and 12 years of experience developing utility-scale solar and wind projects across MENA. She has overseen the development and commissioning of over 800 MW of renewable capacity and leads ClearPath\'s technical and commercial operations.',
    experience: '12 years',
    location: 'Dubai, UAE',
    email: 'a.hassan@clearpath-re.com',
    phone: '+971 4 XXX XXXX',
    linkedin: 'linkedin.com/in/dr-amira-hassan',
    credentials: ['PhD Energy Systems — MIT', 'PE (Professional Engineer)', 'LEED AP', 'IEA Fellow'],
    documents: [
      { name: 'Director Profile & Academic CV', type: 'PDF', size: '510 KB', date: '2025-12-01', category: 'bio' },
      { name: 'Engineering Certifications', type: 'PDF', size: '210 KB', date: '2025-12-01', category: 'credential' },
      { name: 'Technical Operations Report Q2 2026', type: 'PDF', size: '3.2 MB', date: '2026-07-20', category: 'report' },
      { name: 'Director Independence Declaration', type: 'PDF', size: '76 KB', date: '2025-12-05', category: 'legal' },
    ],
    images: [
      { src: '/assets/images/image-1787244773683.png', alt: 'Utility-scale solar PV installation showing rows of panels at ClearPath operational site', caption: 'Solar Asset — Egypt (200 MW)' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Wind turbines at ClearPath onshore wind farm in Jordan against desert landscape', caption: 'Wind Asset — Jordan (80 MW)' },
      { src: '/assets/images/image-1787244773683.png', alt: 'Control room monitoring dashboard showing real-time energy generation data across all assets', caption: 'Operations Control Centre' },
    ],
    schedule: [
      { date: '2026-08-29', time: '13:00 GST', title: 'Final Close Investor Briefing', type: 'webinar', status: 'upcoming', description: 'Final briefing before fund close. Covers operational performance, carbon credits, and exit strategy.' },
      { date: '2026-07-15', time: '14:00 GST', title: 'Technical Operations Webinar', type: 'webinar', status: 'completed', description: 'Deep-dive into asset performance, PPA compliance, and O&M cost management.' },
    ],
  },
};

// Default director for campaigns without specific data
const defaultDirector: ProjectDirector = {
  name: 'Omar Al-Farsi',
  title: 'Project Director',
  avatar: '/assets/images/no_image.png',
  bio: 'Omar is an experienced investment professional with over 10 years in alternative assets and private markets across the GCC. He oversees all operational, financial, and investor relations aspects of this campaign, ensuring compliance with regulatory requirements and delivering transparent reporting to investors.',
  experience: '10+ years',
  location: 'Dubai, UAE',
  email: 'o.alfarsi@crowdvault.ae',
  phone: '+971 4 XXX XXXX',
  linkedin: 'linkedin.com/in/omar-alfarsi',
  credentials: ['CFA Charterholder', 'MBA — AUS', 'CAIA Member'],
  documents: [
    { name: 'Director Profile & CV', type: 'PDF', size: '350 KB', date: '2026-07-01', category: 'bio' },
    { name: 'Professional Credentials', type: 'PDF', size: '140 KB', date: '2026-07-01', category: 'credential' },
    { name: 'Latest Project Update Report', type: 'PDF', size: '1.4 MB', date: '2026-08-01', category: 'report' },
    { name: 'Director Disclosure Statement', type: 'PDF', size: '82 KB', date: '2026-07-01', category: 'legal' },
  ],
  images: [
    { src: '/assets/images/image-1787244773683.png', alt: 'Project overview showing key assets and operational infrastructure', caption: 'Project Overview' },
    { src: '/assets/images/image-1787244773683.png', alt: 'Progress update showing development milestones achieved to date', caption: 'Progress Update' },
  ],
  schedule: [
    { date: '2026-09-01', time: '14:00 GST', title: 'Investor Update Webinar', type: 'webinar', status: 'upcoming', description: 'Regular investor update covering project milestones, financials, and Q&A.' },
    { date: '2026-08-01', time: '14:00 GST', title: 'Q2 Investor Briefing', type: 'webinar', status: 'completed', description: 'Q2 performance review and outlook for H2 2026.' },
  ],
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const docCategoryColors: Record<DirectorDocument['category'], string> = {
  bio: 'bg-blue-500/10 text-blue-400',
  credential: 'bg-positive/10 text-positive',
  report: 'bg-primary/10 text-primary',
  legal: 'bg-warning/10 text-warning',
};

const docCategoryLabels: Record<DirectorDocument['category'], string> = {
  bio: 'Bio',
  credential: 'Credential',
  report: 'Report',
  legal: 'Legal',
};

const scheduleTypeConfig: Record<ScheduleItem['type'], { color: string; label: string }> = {
  webinar: { color: 'bg-primary/10 text-primary border-primary/20', label: 'Webinar' },
  'site-visit': { color: 'bg-positive/10 text-positive border-positive/20', label: 'Site Visit' },
  'q&a': { color: 'bg-accent/10 text-accent border-accent/20', label: 'Live Q&A' },
  meeting: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Meeting' },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProjectDirectorSection({ campaignId }: ProjectDirectorSectionProps) {
  const director = directorData[campaignId] ?? defaultDirector;
  const [activeTab, setActiveTab] = useState<'profile' | 'documents' | 'images' | 'schedule'>('profile');
  const [expandedSchedule, setExpandedSchedule] = useState<number | null>(null);

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'documents' as const, label: 'Documents', icon: FileText, count: director.documents.length },
    { id: 'images' as const, label: 'Gallery', icon: ImageIcon, count: director.images.length },
    { id: 'schedule' as const, label: 'Schedule', icon: Calendar, count: director.schedule.filter(s => s.status === 'upcoming').length },
  ];

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-xl bg-surface-3 border border-border overflow-hidden flex-shrink-0 flex items-center justify-center">
            <User size={24} className="text-muted-foreground" />
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h2 className="text-sm font-bold text-foreground">{director.name}</h2>
                <p className="text-xs text-primary font-medium mt-0.5">{director.title}</p>
              </div>
              <span className="text-xs bg-surface-3 text-muted-foreground px-2.5 py-1 rounded-full flex items-center gap-1.5 flex-shrink-0">
                <Award size={11} className="text-primary" /> {director.experience} experience
              </span>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={10} />{director.location}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Mail size={10} />{director.email}</span>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-4 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  isActive
                    ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-surface-3'
                }`}
              >
                <Icon size={12} />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-mono-data ${isActive ? 'bg-white/20 text-white' : 'bg-surface-4 text-muted-foreground'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-5">

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Biography</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{director.bio}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Credentials & Qualifications</p>
              <div className="flex flex-wrap gap-2">
                {director.credentials.map((cred) => (
                  <span key={cred} className="text-xs bg-surface-3 border border-border text-foreground px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                    <Award size={10} className="text-primary flex-shrink-0" />
                    {cred}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <a href={`mailto:${director.email}`} className="flex items-center gap-2.5 p-3 bg-surface-3 rounded-lg hover:bg-surface-4 transition-colors group cursor-pointer">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={13} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-xs font-medium text-foreground truncate group-hover:text-primary transition-colors">{director.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-2.5 p-3 bg-surface-3 rounded-lg">
                <div className="w-7 h-7 bg-surface-4 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone size={13} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-xs font-medium text-foreground">{director.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 p-3 bg-surface-3 rounded-lg hover:bg-surface-4 transition-colors group cursor-pointer">
                <div className="w-7 h-7 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <LinkedinIcon size={13} className="text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">LinkedIn</p>
                  <p className="text-xs font-medium text-foreground truncate group-hover:text-blue-400 transition-colors">View Profile</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-2">
            {director.documents.map((doc) => (
              <div key={doc.name} className="flex items-center justify-between p-3 bg-surface-3 rounded-lg hover:bg-surface-4 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={14} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{doc.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${docCategoryColors[doc.category]}`}>
                        {docCategoryLabels[doc.category]}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{doc.type} · {doc.size} · {doc.date}</p>
                  </div>
                </div>
                <Download size={14} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {director.images.map((img, i) => (
              <div key={i} className="group relative rounded-xl overflow-hidden bg-surface-3 cursor-pointer">
                <div className="relative h-40 w-full">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-xs font-medium text-white">{img.caption}</p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-7 h-7 bg-black/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <ArrowUpRight size={13} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-3">
            {director.schedule.map((item, i) => {
              const typeCfg = scheduleTypeConfig[item.type];
              const isExpanded = expandedSchedule === i;
              return (
                <div key={i} className={`rounded-xl border transition-all ${item.status === 'completed' ? 'border-border bg-surface-3/50 opacity-70' : 'border-border bg-surface-3 hover:border-primary/30'}`}>
                  <button
                    className="w-full flex items-start gap-3 p-3.5 text-left"
                    onClick={() => setExpandedSchedule(isExpanded ? null : i)}
                  >
                    {/* Status dot */}
                    <div className="flex-shrink-0 mt-0.5">
                      {item.status === 'completed' ? (
                        <CheckCircle size={15} className="text-positive" />
                      ) : item.status === 'live' ? (
                        <div className="w-3.5 h-3.5 rounded-full bg-critical animate-pulse" />
                      ) : (
                        <Circle size={15} className="text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${item.status === 'completed' ? 'text-muted-foreground' : 'text-foreground'}`}>{item.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${typeCfg.color}`}>{typeCfg.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={10} />{item.date}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} />{item.time}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-muted-foreground">
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="border-t border-border pt-3">
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                        {item.status === 'upcoming' && (
                          <button className="mt-3 btn-primary text-xs gap-1.5 py-1.5">
                            <Calendar size={11} /> Register / Add to Calendar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
